// ============================================================
// HIT OUTCOME - The last half of one hit: the damage that lands
// (the protagonist floor and ceiling, a `dmgCap` exposure), Righteous
// Fury, Merciful, and the critical it causes. Draws the fury's d100
// and d5 - the last dice a hit rolls, in that order.
// ============================================================
import { rollDie, rollD100 } from './dice';
import { hasWS, getWSP } from './weaponSpecials';
import { survivableSeverity } from './survival/critCap';
import type { Protagonist } from './survival/protagonist';
import { OBJECT_DESTROYED, getCritForBody } from './criticalTables';
import type { Target, DiceResult, CritResult } from '../types';
import type { CalcSingleHitParams, HitContext } from './hitDamageTypes';
import type { HitSoak } from './hitSoak';

/** The damage that lands, before Merciful, and what the clamps did to it. */
export interface LandedDamage {
  effectiveDamage: number;
  damageFloorApplied: number;
  exposureDmgCap: number;
}

/**
 * THE ANTI-POPCORN PAIR. Subtractive soak is why an ork slugga does
 * literally zero to a marine forever and a krak missile does 26: the
 * distribution has no middle. The floor puts one there; the ceiling
 * takes the tail off.
 *
 * The ceiling is A FRACTION OF MAXIMUM WOUNDS, never a wound count: a
 * flat 6 means different things to a 22-wound marine and a 12-wound
 * guardsman, and the GM would be retuning it per character forever.
 * Floored at 1 so a small fraction of a small pool cannot round away to
 * invulnerability. PROTECTION OUTRANKS PRESSURE: applied after the floor,
 * so a GM who sets both badly gets the safe answer rather than an
 * incoherent one. `damageFloorApplied` is what the floor was worth AFTER
 * the ceiling had its say - the chip limit refunds exactly this, so it
 * must be the real difference rather than the raw floor value.
 */
function protagonistClamp(damage: number, prot: Protagonist, target: Target) {
  const floored = prot.damageFloor ? Math.max(prot.damageFloor, damage) : damage;
  if (!prot.damageCeiling) return { effectiveDamage: floored, damageFloorApplied: floored - damage };
  const cap = Math.max(1, Math.floor(prot.damageCeiling * (target.wounds?.max || 0)));
  const effectiveDamage = Math.min(cap, floored);
  return { effectiveDamage, damageFloorApplied: effectiveDamage - Math.min(cap, damage) };
}

/**
 * Raw damage through the soak, then the clamps. `blocked` is excluded
 * from the protagonist pair because an attack that genuinely cannot
 * touch this target must stay at zero - the floor is a soak-wall fix,
 * not a licence to wound the untouchable. Hordes are excluded because
 * they take magnitude on another path and the clamps are about wounds.
 * Last, a `dmgCap` exposure (v8.368.1): no single hit deals more than
 * this to them.
 */
export function landedDamage(ctx: HitContext, target: Target, rawDamage: number, soak: HitSoak): LandedDamage {
  const soaked = soak.blocked ? 0 : Math.max(0, rawDamage + soak.forceBonus - soak.effectiveAP - soak.tb);
  const clamped =
    ctx.prot && !soak.blocked && !ctx.isHorde
      ? protagonistClamp(soaked, ctx.prot, target)
      : { effectiveDamage: soaked, damageFloorApplied: 0 };
  const cap = ctx.exposed.dmgCap;
  if (!(cap > 0 && clamped.effectiveDamage > cap)) return { ...clamped, exposureDmgCap: 0 };
  return { effectiveDamage: cap, damageFloorApplied: clamped.damageFloorApplied, exposureDmgCap: cap };
}

/** Righteous Fury's side of the hit, in the order `HitResult` reports it. */
export interface FuryRoll {
  righteousFury: boolean;
  rfConfirmed: boolean;
  rfAutoConfirmed: boolean;
  rfRoll: number;
  rfSeverity: number;
}

const NO_FURY: FuryRoll = {
  righteousFury: false,
  rfConfirmed: false,
  rfAutoConfirmed: false,
  rfRoll: 0,
  rfSeverity: 0,
};

/**
 * Whether any d10 shows the fury threshold. Vengeful(X) lowers it, and
 * so does a `fury` exposure (v8.350.0, a threshold since v8.368.1) - the
 * lower threshold wins.
 */
function showsFury(roll: DiceResult, ctx: HitContext): boolean {
  const vengeful = hasWS(ctx.sp, 'Vengeful') ? getWSP(ctx.sp, 'Vengeful') : 10;
  const threshold = Math.min(vengeful, ctx.exposed.furyThreshold ?? 10);
  return roll.results.some(
    (g) => g.type !== 'flat' && g.sides === 10 && g.rolls && g.rolls.some((r: number) => r >= threshold),
  );
}

/**
 * Righteous Fury / Vengeful / Hatred. The d100 is rolled whenever the
 * fury shows - still rolled for log continuity even when auto-confirming
 * - and the d5 severity only when it confirms.
 */
export function rollFury(params: CalcSingleHitParams, ctx: HitContext, dmgRoll: DiceResult): FuryRoll {
  const { slot, hatred, autoConfirmRF = false } = params;
  const enabled = slot.righteousFury || hasWS(ctx.sp, 'Vengeful') || !!hatred;
  if (!enabled || !showsFury(dmgRoll, ctx)) return { ...NO_FURY };
  const rfRoll = rollD100();
  const naturalConfirm = !!hatred || rfRoll <= slot.skillValue;
  // Cursed (v8.286.0): the weapon's own malice confirms the fury.
  const cursedConfirm = hasWS(ctx.sp, 'Cursed');
  const rfConfirmed = naturalConfirm || autoConfirmRF || cursedConfirm;
  // Flag only when the training made the difference (Deathwatch Training vs Xenos)
  const rfAutoConfirmed = rfConfirmed && !naturalConfirm && !cursedConfirm;
  const rfSeverity = rfConfirmed ? rollDie(5) : 0;
  return { righteousFury: true, rfConfirmed, rfAutoConfirmed, rfRoll, rfSeverity };
}

/** The hit's critical, and the damage after Merciful. */
export interface CritOutcome {
  effectiveDamage: number;
  crit: CritResult | null;
  critSev: number;
  exposureCrit: number;
}

type CritPart = Pick<CritOutcome, 'crit' | 'critSev' | 'exposureCrit'>;

/** The table's row, unless Stuff of Nightmares shrugs it off (it cannot below severity 9). */
function critRow(params: CalcSingleHitParams, ctx: HitContext, severity: number): CritResult {
  if (params.target.stuffOfNightmares && severity < 9) {
    return { text: 'Stuff of Nightmares: immune.', fx: '', isDead: false };
  }
  return getCritForBody(ctx.damageType, params.location.id, severity, ctx.bodyType);
}

/**
 * The severity of a wound that went past zero. Deadly(X) (v8.286.0) adds
 * X BEFORE True Grit and the caps, so the defensive layers still have
 * their say - the tables are not monotonic, deeper is not reliably worse,
 * and that is the gamble the rule sells. A `crit` exposure (v8.368.1):
 * Brittle deepens, Hardy softens - like Deadly, before the caps.
 *
 * True Grit halves it, ROUNDING UP, minimum 1. trueGrit is the SINGLE
 * mechanical switch (v6.34.4): Astartes only cascades onto it at
 * activation edges, so the GM can disable True Grit independently and be
 * respected. The protagonist cap is the HARD ceiling, applied after True
 * Grit so the two compose rather than compete. It is not the guarantee:
 * some rows below any plausible cap are still fatal (Explosive body at
 * 5), which is what `deadlySeveritiesUpTo` tells the GM and what the
 * death floor exists to catch.
 */
function woundSeverity(params: CalcSingleHitParams, ctx: HitContext, damage: number): number {
  const { runningWounds, target } = params;
  let sev = runningWounds > 0 ? damage - runningWounds : damage;
  if (sev < 1) sev = 1;
  sev += getWSP(ctx.sp, 'Deadly');
  sev = Math.max(1, sev + ctx.exposed.crit);
  if (target.trueGrit) sev = Math.max(1, Math.ceil(sev / 2));
  return Math.min(sev, ctx.prot?.critSeverityCap ?? 10, 10);
}

/** Does this hit take the target past 0 Wounds? What a critical needs - and an object's destruction. */
const pastZero = (params: CalcSingleHitParams, damage: number) => params.runningWounds - damage < 0 && damage > 0;

/** The critical a wound past zero causes; none when the hit leaves the target standing. */
function woundCrit(params: CalcSingleHitParams, ctx: HitContext, damage: number): CritPart {
  if (!pastZero(params, damage)) return { crit: null, critSev: 0, exposureCrit: 0 };
  const critSev = woundSeverity(params, ctx, damage);
  return { crit: critRow(params, ctx, critSev), critSev, exposureCrit: ctx.exposed.crit };
}

/**
 * The critical a confirmed fury causes when the wound itself caused none.
 * RIGHTEOUS FURY RESTRAINT: RF severity is 1d5, so it can never reach the
 * instant-death rows - the danger is subtler and worse: a bare `Dy`
 * ("dies without treatment", no clock) at Explosive body 5, landed on a
 * character at FULL wounds who was never even scratched. Stepped DOWN to
 * the nearest survivable row rather than cancelled, so Righteous Fury
 * still means something.
 */
function furyCrit(params: CalcSingleHitParams, ctx: HitContext, rfSeverity: number, prior: CritPart): CritPart {
  const capped = Math.min(rfSeverity, ctx.prot?.critSeverityCap ?? 10);
  const rfSev = ctx.prot?.rfRestraint
    ? (survivableSeverity(ctx.damageType, params.location.id, capped, ctx.bodyType) ?? 0)
    : capped;
  const reaches = rfSev > 0 && (!params.target.stuffOfNightmares || rfSev >= 9);
  const crit = reaches ? getCritForBody(ctx.damageType, params.location.id, rfSev, ctx.bodyType) : prior.crit;
  return { crit, critSev: rfSev, exposureCrit: prior.exposureCrit };
}

/** An object's only critical: destroyed, once the hit takes it past 0 Wounds. */
function objectCrit(params: CalcSingleHitParams, damage: number): CritPart {
  const destroyed = pastZero(params, damage);
  return { crit: destroyed ? OBJECT_DESTROYED : null, critSev: 0, exposureCrit: 0 };
}

/**
 * Merciful (v8.286.0): the capture weapon - damage stops at the wounds
 * the target has left, so nobody drops below zero and no crit fires.
 * Stabilised at 0, alive by construction. Hordes skip crits entirely.
 */
export function critOutcome(params: CalcSingleHitParams, ctx: HitContext, damage: number, fury: FuryRoll): CritOutcome {
  const merciful = hasWS(ctx.sp, 'Merciful');
  const effectiveDamage = merciful ? Math.min(damage, Math.max(0, params.runningWounds)) : damage;
  if (ctx.isHorde || merciful) return { effectiveDamage, crit: null, critSev: 0, exposureCrit: 0 };
  // An object (2026-10-26) is destroyed at once past 0 Wounds - no table, and no fury: a fury may wound it, never
  // destroy an object that still has Wounds left.
  if (ctx.bodyType === 'object') return { effectiveDamage, ...objectCrit(params, effectiveDamage) };
  const wound = woundCrit(params, ctx, effectiveDamage);
  const part = fury.rfConfirmed && !wound.crit ? furyCrit(params, ctx, fury.rfSeverity, wound) : wound;
  return { effectiveDamage, ...part };
}
